import { uiPaper } from "shared/ui/uiSurface"
import {
  Close,
  Delete,
  Edit,
  LockReset,
  PersonOff,
  PersonOutline,
} from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Employee,
  useDeleteEmployeeMutation,
  useResetEmployeePasswordMutation,
  useUpdateEmployeeMutation,
} from "../../apis/appApi"

type EmployeeDetailDrawerProps = {
  employee: Employee | null
  open: boolean
  onClose: () => void
  onEdit: (employee: Employee) => void
}

const InfoRow: FC<{
  label: string
  value: React.ReactNode
  "data-testid"?: string
}> = ({ label, value, "data-testid": testId }) => (
  <Stack direction="row" spacing={1} data-testid={testId}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
      {label}:
    </Typography>
    <Typography variant="body2">{value ?? "\u2014"}</Typography>
  </Stack>
)

export const EmployeeDetailDrawer: FC<EmployeeDetailDrawerProps> = ({
  employee,
  open,
  onClose,
  onEdit,
}) => {
  const { t } = useTranslation()
  const [updateEmployee] = useUpdateEmployeeMutation()
  const [deleteEmployee] = useDeleteEmployeeMutation()
  const [resetPassword, { isLoading: isResetting }] =
    useResetEmployeePasswordMutation()

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: "success" | "error"
  }>({ open: false, message: "", severity: "success" })

  const handleEnableToggle = useCallback(async () => {
    if (!employee) return
    try {
      await updateEmployee({
        id: employee.id,
        enabled: !employee.enabled,
      }).unwrap()
    } catch (error) {
      console.error("Failed to toggle enabled:", error)
    }
  }, [employee, updateEmployee])

  const handleDelete = useCallback(async () => {
    if (!employee) return
    try {
      await deleteEmployee(employee.id).unwrap()
      setDeleteConfirmOpen(false)
      onClose()
    } catch (error) {
      console.error("Failed to delete employee:", error)
    }
  }, [employee, deleteEmployee, onClose])

  const handleResetPassword = useCallback(async () => {
    if (!employee) return
    try {
      await resetPassword(employee.id).unwrap()
      setResetConfirmOpen(false)
      setSnackbar({
        open: true,
        message: t("employees.resetPasswordSent"),
        severity: "success",
      })
    } catch (error) {
      console.error("Failed to reset password:", error)
      setResetConfirmOpen(false)
      setSnackbar({
        open: true,
        message: t("employees.resetPasswordError"),
        severity: "error",
      })
    }
  }, [employee, resetPassword, t])

  return (
    <>
      <Drawer
        data-testid="employee-detail-drawer"
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: {
            ...uiPaper("UI-DLV2-112"),
            sx: { width: 550, maxWidth: "100vw" },
          },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">
              {employee
                ? `${t("employees.detail.title")} — ${employee.firstName} ${
                    employee.lastName
                  }`
                : t("loading")}
            </Typography>
            {employee && (
              <Chip
                data-testid="employee-detail-status"
                label={
                  employee.enabled
                    ? t("employees.active")
                    : t("employees.disabled")
                }
                color={employee.enabled ? "success" : "default"}
                size="small"
              />
            )}
          </Stack>
          <Stack direction="row" spacing={0.5}>
            {employee && (
              <IconButton
                data-testid="employee-edit-btn"
                size="small"
                onClick={() => onEdit(employee)}
              >
                <Edit fontSize="small" />
              </IconButton>
            )}
            <IconButton
              data-testid="employee-close-btn"
              onClick={onClose}
              size="small"
            >
              <Close />
            </IconButton>
          </Stack>
        </Stack>

        <Divider />

        {employee && (
          <Box sx={{ p: 2, overflow: "auto", flex: 1 }}>
            {/* Employee Info */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t("employees.detail.info")}
            </Typography>
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              <InfoRow
                data-testid="employee-detail-firstName"
                label={t("employees.firstName")}
                value={employee.firstName}
              />
              <InfoRow
                data-testid="employee-detail-lastName"
                label={t("employees.lastName")}
                value={employee.lastName}
              />
              <InfoRow
                data-testid="employee-detail-username"
                label={t("employees.username")}
                value={employee.username}
              />
              <InfoRow
                label={t("employees.status")}
                value={
                  <Chip
                    label={
                      employee.enabled
                        ? t("employees.active")
                        : t("employees.disabled")
                    }
                    color={employee.enabled ? "success" : "default"}
                    size="small"
                  />
                }
              />
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Roles */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t("employees.detail.roles")}
            </Typography>
            {employee.roles && employee.roles.length > 0 ? (
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 3 }}
              >
                {employee.roles.map((r, index) => (
                  <Chip
                    data-testid={`employee-role-${index}`}
                    key={r.id}
                    label={r.role}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            ) : (
              <Typography
                data-testid="employee-detail-no-roles"
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                {t("employees.detail.noRoles")}
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Security section */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t("employees.detail.security")}
            </Typography>
            <Stack spacing={1} sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {t("employees.resetPasswordDesc")}
              </Typography>
              <Box>
                <Button
                  data-testid="employee-reset-password-btn"
                  variant="outlined"
                  size="small"
                  startIcon={<LockReset />}
                  onClick={() => setResetConfirmOpen(true)}
                  disabled={isResetting}
                >
                  {t("employees.resetPassword")}
                </Button>
              </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Footer actions */}
            <Stack direction="row" spacing={1}>
              <Button
                data-testid="employee-footer-edit-btn"
                variant="outlined"
                size="small"
                startIcon={<Edit />}
                onClick={() => onEdit(employee)}
              >
                {t("edit")}
              </Button>
              <Button
                data-testid="employee-toggle-enabled-btn"
                variant="outlined"
                size="small"
                startIcon={employee.enabled ? <PersonOff /> : <PersonOutline />}
                onClick={handleEnableToggle}
              >
                {employee.enabled
                  ? t("employees.disable")
                  : t("employees.enable")}
              </Button>
              <Button
                data-testid="employee-delete-btn"
                variant="outlined"
                size="small"
                color="error"
                startIcon={<Delete />}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                {t("delete")}
              </Button>
            </Stack>
          </Box>
        )}
      </Drawer>

      {/* Delete confirmation dialog */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-113") }}
        data-testid="employee-delete-dialog"
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle>{t("employees.deleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("employees.deleteConfirm")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid="employee-delete-cancel-btn"
            onClick={() => setDeleteConfirmOpen(false)}
          >
            {t("cancel")}
          </Button>
          <Button
            data-testid="employee-delete-confirm-btn"
            onClick={handleDelete}
            color="error"
            variant="contained"
          >
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset password confirmation dialog */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-114") }}
        data-testid="employee-reset-password-dialog"
        open={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle>{t("employees.resetPasswordTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("employees.resetPasswordConfirm", {
              name: employee
                ? `${employee.firstName} ${employee.lastName}`
                : "",
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid="employee-reset-cancel-btn"
            onClick={() => setResetConfirmOpen(false)}
          >
            {t("cancel")}
          </Button>
          <Button
            data-testid="employee-reset-confirm-btn"
            onClick={handleResetPassword}
            variant="contained"
            disabled={isResetting}
          >
            {t("employees.resetPassword")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}
